
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LocalStorageService } from './local.storage.service';
import { STORAGE_KEYS } from '../class/storage-keys';

/**
 * Key used to access LocalStorage BackNavigation object
 */
const KEY = STORAGE_KEYS.REVERSE_ROUTER.key;

/** ReverseRouterService config */
interface BackNavigationConfig {
  /** The url to navigate back */
  urlSegment: string[],

  /** The partial value of an component, e.g: FormGroup values not submited */
  partialData: any;
}

export interface RouteConfig {
  /** Key name of the component you are goig/exiting to  */
  component: string,

  /** The url to navigate back */
  address: string[],

  /** The current form value to be retrieved after navigate / navigate back */
  partialData: any
}



@Injectable({
  providedIn: 'root'
})
export class ReverseRouterService {

  private _backNavigation: Map<string, BackNavigationConfig>;

  /** Max persisted component entries; least-recently-used are evicted beyond this. */
  private static readonly MAX_ENTRIES = 5;

  constructor(
    private router: Router,
    private localStorageService: LocalStorageService
  ) {
    this._backNavigation = new Map()
    this.getStorage()
  }

  private getStorage() {
    const config = this.localStorageService.get(KEY)
    //const config = localStorage.getItem(KEY);
    if (config) {
      const arr = JSON.parse(config)
      if (arr.entries) {
        this._backNavigation = new Map(arr);
      }
    }
  }

  private get backNavigation(): Map<string, BackNavigationConfig> {
    return this._backNavigation;
  }

  private set backNavigation(value: Map<string, BackNavigationConfig>) {
    this._backNavigation = value;
    if (value) {
      this._evictBeyondCap(value);
      this._persist(value);
    } else {
      this.localStorageService.remove(KEY)
    }
  }

  /**
   * Move a key to the most-recently-used position. A JS `Map` keeps insertion
   * order, and re-`set`ting an existing key does NOT reorder it, so we delete
   * then set to push it to the newest position.
   */
  private _touch(map: Map<string, BackNavigationConfig>, key: string, value: BackNavigationConfig) {
    map.delete(key);
    map.set(key, value);
  }

  /** Evict least-recently-used entries (oldest insertion order) beyond the cap. */
  private _evictBeyondCap(map: Map<string, BackNavigationConfig>) {
    while (map.size > ReverseRouterService.MAX_ENTRIES) {
      const oldest = map.keys().next().value;
      map.delete(oldest);
    }
  }

  /** Serialize entries, dropping `partialData` as a fallback on circular-reference errors. */
  private _serializeEntries(entries: Array<[string, BackNavigationConfig]>): string {
    try {
      return JSON.stringify(entries);
    } catch (error) {
      // Ticket#2026051471000239 - Deleting partialData to avoid circular reference error in JSON.stringify
      console.error(error);
      for (const [, item] of entries) {
        if (item && item.partialData !== undefined) {
          delete item.partialData;
        }
      }
      return JSON.stringify(entries);
    }
  }

  /**
   * Quota-safe persist with progressive degradation:
   * 1. try full payload,
   * 2. on QuotaExceededError drop the oldest entry's `partialData` (keep `urlSegment`), retry oldest-first,
   * 3. last resort: persist `urlSegment`-only for retained entries,
   * 4. if still failing: log and keep the in-memory map intact — never throw.
   */
  private _persist(map: Map<string, BackNavigationConfig>) {
    // Shallow-clone entry values so quota degradation never mutates the live in-memory map.
    const entries: Array<[string, BackNavigationConfig]> = Array.from(map.entries())
      .map(([key, value]) => [key, { urlSegment: value.urlSegment, partialData: value.partialData }] as [string, BackNavigationConfig]);

    let dropIndex = 0;
    const onQuota = () => {
      // Drop the oldest remaining partialData (keep urlSegment), oldest-first.
      while (dropIndex < entries.length) {
        const item = entries[dropIndex++][1];
        if (item && item.partialData !== undefined) {
          delete item.partialData;
          return this._serializeEntries(entries);
        }
      }
      return false; // nothing left to drop: all entries are already urlSegment-only
    };

    const ok = this.localStorageService.setSafe(KEY, this._serializeEntries(entries), onQuota);
    if (!ok) {
      // Irrecoverable: keep the in-memory map usable for this session, do not throw.
      console.error('ReverseRouterService: unable to persist back-navigation state (quota exceeded).');
    }
  }

  /**
   * Get the partial data of latest route navigated to and delete it 
   * 
   * @param component The key representing ReverseRouterService with partial data
   * @param remove Remove key from ReverseRouterService config after get it
  */
  public getPartialDataOf(component: string, remove?: boolean) {
    if (this.backNavigation) {
      let route = this.backNavigation.get(component);
      if (remove) {
        this.remove(component)
      }
      if (route) {
        if (!remove) {
          // Reading an entry marks it most-recently-used so it survives LRU eviction.
          this._touch(this.backNavigation, component, route);
        }
        return route.partialData;
      }
    }
    if (remove) {
      this.remove(component)
    }
    return undefined
  }

  /** Navigate back using ReverseRouterService config
   * 
   * @param from Key for the current url in view
   * @param fallbackUrl A default url to back to, in case of ReverseRouterService
   *  haven't the config to navigate back
  */
  public goBack(from: string, fallbackUrl: string[]) {
    if (this.backNavigation) {
      const route = this.backNavigation.get(from);
      if(route && route.urlSegment && route.urlSegment.length){
        this.router.navigateByUrl(route.urlSegment[0]);
      } else {
        this.router.navigate(fallbackUrl);
      }
    } else {
      this.router.navigate(fallbackUrl);
    }
  }

  /**
   * Navigate forward preparing to navigate back
   * 
   * @param from The config of the component you are exiting
   * @param to The config of the component you are navigating to
   */
  public navigate(from: RouteConfig, to: RouteConfig) {
    let returnTo = [];
    const backNavigation = this.backNavigation;
    const middle = this.backNavigation.get(from.component)
    if(middle){
      returnTo = middle.urlSegment;
    }
    // Touch both components to most-recently-used so they are retained under the LRU cap.
    this._touch(this.backNavigation, from.component, {
      urlSegment: returnTo,
      partialData: from.partialData
    })
    this._touch(this.backNavigation, to.component, {
      urlSegment: from.address,
      partialData: to.partialData
    });

    this.backNavigation = backNavigation;
    this.router.navigate(to.address)
  }

  /**
   * Set a partial data to an component
   * 
   * You don't need call `ReverseRouterService.setPartial()` before call 
   * `ReverseRouterService.navigate()`.
   * 
   * @param component The component name
   * @param partialData The partial data object
   */
  public setPartial(component: string, partialData: any) {
    const previousData: any = this.backNavigation.get(component) || {};
    previousData['partialData'] = partialData;
    this._touch(this.backNavigation, component, previousData);
    // this.backNavigation = this.backNavigation;
  }

  /** Clear the ReverseRouterService config of an component*/
  public remove(component: string) {
    if (this.backNavigation) {
      this.backNavigation.delete(component);
      // this.backNavigation = this.backNavigation
    }
  }

  public clear(){
    this.backNavigation = new Map();
  }

}

// import { Injectable } from '@angular/core';

// @Injectable({
//   providedIn: 'root'
// })
// export class ReverseRouterService {

//   constructor() { }
// }
